import api from '@/services/axiosApi'

/**
 * Helper to determine if a partner uses V2 endpoints
 * V2 is used when integrationVersion is 'v2' (existing V2 integration)
 */
const isV2Integration = (partner: Partner | undefined): boolean => {
  return partner?.integrationVersion === 'v2'
}

export default {
  createCustomField: {
    mutationFn: (teamPid: number, label: string) => {
      const { integratedPartner } = useIntegrationStore()

      const endpoint = isV2Integration(integratedPartner)
        ? `/api/${teamPid}/connections/${integratedPartner?.name}/custom_fields`
        : `/api/${teamPid}/integrations/${integratedPartner?.name}/custom_fields`

      return api.post(endpoint, { label })
    }
  },
  native_fields: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (integratedPartner?.espFeatureSupport?.customField?.unstructured) {
        return []
      }

      if (isV2Integration(integratedPartner)) {
        return api
          .get(
            `/api/${teamPid}/connections/${integratedPartner?.name}/native_fields`
          )
          .then((response) => response.data.native_fields)
      }

      return api
        .get(
          `/api/${teamPid}/integrations/${integratedPartner?.name}/native_fields`
        )
        .then((response) => response.data.native_fields)
    }
  },
  custom_fields: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (integratedPartner?.espFeatureSupport?.customField?.unstructured) {
        return []
      }

      if (isV2Integration(integratedPartner)) {
        return api
          .get(
            `/api/${teamPid}/connections/${integratedPartner?.name}/custom_fields`
          )
          .then((response) => {
            return response.data.custom_fields
          })
      }

      return api
        .get(
          `/api/${teamPid}/integrations/${integratedPartner?.name}/custom_fields`
        )
        .then((response) => response.data.custom_fields)
    }
  },
  lists: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (!integratedPartner?.espFeatureSupport?.list) {
        return []
      }

      if (isV2Integration(integratedPartner)) {
        return api
          .get(`/api/${teamPid}/connections/${integratedPartner?.name}/lists`)
          .then((response) => {
            return response.data.lists
          })
      }

      return api
        .get(`/api/${teamPid}/integrations/${integratedPartner?.name}/lists`)
        .then((response) => response.data.lists)
    }
  },
  tags: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (!integratedPartner?.espFeatureSupport?.tag) {
        return []
      }

      if (isV2Integration(integratedPartner)) {
        return api
          .get(`/api/${teamPid}/connections/${integratedPartner?.name}/tags`)
          .then((response) => response.data.tags)
      }

      return api
        .get(`/api/${teamPid}/integrations/${integratedPartner?.name}/tags`)
        .then((response) => response.data.tags)
    }
  },
  forms: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (isV2Integration(integratedPartner)) {
        return api
          .get(`/api/${teamPid}/connections/${integratedPartner?.name}/forms`)
          .then((response) => response.data.forms)
      }

      return api
        .get(`/api/${teamPid}/integrations/${integratedPartner?.name}/forms`)
        .then((response) => response.data.forms)
    }
  },
  available_events: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (!integratedPartner?.espFeatureSupport?.eventHistory) {
        return []
      }

      // Only V2 integrations support event history
      if (!isV2Integration(integratedPartner)) {
        return []
      }

      return api
        .get(
          `/api/${teamPid}/connections/${integratedPartner?.name}/available-events`
        )
        .then((response) => response.data.events)
    }
  },
  available_products: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (!integratedPartner?.espFeatureSupport?.purchaseHistory) {
        return { products: [], supportsRevenue: false }
      }

      // Only V2 integrations support purchase history
      if (!isV2Integration(integratedPartner)) {
        return { products: [], supportsRevenue: false }
      }

      return api
        .get(
          `/api/${teamPid}/connections/${integratedPartner?.name}/available-products`
        )
        .then((response) => ({
          products: response.data.products,
          supportsRevenue: response.data.supportsRevenue
        }))
    }
  }
}
